import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { Icon } from './Icon';
import { pinSrcFor } from './MapPin';

/**
 * How far a panel sharing this corner must drop to clear the chip: its 32px
 * height plus the gap. Exported so no host re-derives the number.
 */
export const HIDDEN_PIN_TYPES_CHIP_STACK = 40;

/**
 * CLUB-667: says that the map is filtered, and undoes it.
 *
 * The pin-type filter is shared across every read-only surface through
 * localStorage, so a set hidden on the Map tab silently changes the full-screen
 * map and the next printed sheet. A filter the user cannot see they set is
 * indistinguishable from missing data — which is the failure this chip exists
 * to prevent.
 *
 * Clicking the count opens the list of what is hidden, each row restoring one
 * type. "Show all" is the blunt undo; this is the one for a user who hid six
 * things and wants five of them back, without hunting for them across three
 * collapsed groups in the settings panel.
 */

export function HiddenPinTypesChip({
  hiddenPinTypes,
  withBackground = true,
  onShowAll,
  onShowType,
  onOpenList,
}: {
  hiddenPinTypes: readonly string[];
  /** Matches the map's own pin drawing, so the list looks like what returns. */
  withBackground?: boolean;
  onShowAll: () => void;
  onShowType: (type: string) => void;
  /**
   * Called when the list opens. The list occupies the space the panels were
   * dropped into, so the host closes them — the same mutual exclusion the
   * layers picker and the appearance panel already keep with each other.
   */
  onOpenList?: () => void;
}) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Derived, not a prop: a `count` passed alongside the set is a second source
  // of one truth, and a caller that updates one and not the other gets a badge
  // that disagrees with the list under it.
  const count = hiddenPinTypes.length;

  // Dismiss on an outside press or Escape. The chip floats over a map the user
  // is panning, so a list that stayed open would sit in the way of the thing
  // they just un-hid.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The last row restoring itself empties the list, so close with it.
  useEffect(() => {
    if (count === 0) setOpen(false);
  }, [count]);

  if (count <= 0) return null;

  const hidden = hiddenPinTypes;

  return (
    <div
      ref={rootRef}
      data-map-print="hide"
      data-testid="hidden-pin-types-chip"
      style={{
        position: 'absolute',
        // Always the top-right corner, on every surface. It is the same badge
        // reporting the same thing, so it belongs in the same place whether you
        // are on the Map tab, the print preview or inside the editor — moving
        // it to the far side on print made the reader hunt for it. Whatever
        // else wants this column is dropped below by
        // HIDDEN_PIN_TYPES_CHIP_STACK, so the two stack rather than overlap.
        top: 12,
        right: 60,
        // Above the appearance panel (10) and its backdrop (9). The backdrop
        // covers the whole map, so at a lower index the chip was not merely
        // behind an open panel but unclickable through it — a press landed on
        // the backdrop and dismissed the panel instead.
        zIndex: 11,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 6,
        maxWidth: 'calc(100% - 80px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: 32,
          padding: '0 4px 0 10px',
          borderRadius: 999,
          background: COLORS.surface,
          border: `1px solid ${COLORS.outlineVariant}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        }}
      >
        {/* Two real buttons: the label opens the list, "Show all" clears
            everything. Both are things you might want, and both need keyboard
            reach. */}
        <button
          type="button"
          onClick={() => {
            // Read `open` rather than deciding inside the updater: React
            // requires updaters to be pure and StrictMode double-invokes them,
            // which would fire onOpenList twice per click.
            if (!open) onOpenList?.();
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
            background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer',
            font: 'inherit', fontSize: 12, color: COLORS.onSurface, whiteSpace: 'nowrap',
          }}
        >
          <span style={{ display: 'flex', color: COLORS.onSurfaceVariant }}>
            <Icon name="eye-off" size={14} />
          </span>
          {t('appearance.pinTypes.hiddenChip', { count })}
          {/* No chevron-up in the icon set — rotate the down one, as the
              group rows in the settings panel do. */}
          <span style={{
            display: 'flex', color: COLORS.onSurfaceVariant,
            transform: open ? 'rotate(180deg)' : undefined,
          }}>
            <Icon name="chevron-down" size={14} />
          </span>
        </button>
        <button
          type="button"
          onClick={onShowAll}
          style={{
            background: COLORS.surfaceContainerHigh, border: 'none', borderRadius: 999,
            padding: '5px 10px', cursor: 'pointer', font: 'inherit',
            fontSize: 12, fontWeight: 600, color: COLORS.primary, whiteSpace: 'nowrap',
          }}
        >
          {t('appearance.pinTypes.showAll')}
        </button>
      </div>

      {open && (
        <div
          data-testid="hidden-pin-types-list"
          style={{
            width: 232,
            maxHeight: 260,
            overflowY: 'auto',
            padding: 6,
            borderRadius: 10,
            background: COLORS.surface,
            border: `1px solid ${COLORS.outlineVariant}`,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{
            fontSize: 10, fontWeight: 700, color: COLORS.onSurfaceVariant,
            textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 6px 6px',
          }}>{t('appearance.pinTypes.hiddenListTitle')}</div>
          {hidden.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onShowType(id)}
              aria-label={t('appearance.pinTypes.show', { label: catalog.label(id) })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '6px', background: 'none', border: 'none', borderRadius: 6,
                cursor: 'pointer', font: 'inherit', textAlign: 'left',
                color: COLORS.onSurface,
              }}
            >
              <img
                src={pinSrcFor(id, withBackground)}
                width={18}
                height={18}
                alt=""
                aria-hidden
                style={{ flex: '0 0 auto', objectFit: 'contain', opacity: 0.45 }}
              />
              <span style={{
                flex: 1, minWidth: 0, fontSize: 12.5,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{catalog.label(id)}</span>
              <span aria-hidden style={{ display: 'flex', color: COLORS.primary }}>
                <Icon name="eye" size={14} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
